import SimpleFilterSelect from 'components/forms/specific/SimpleFilterSelect'
import SelectedElementsModal from 'components/modal/SelectedElementsModal'
import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import MultiContributorsAutocomplete from 'referencial/components/contributor/components/MultiContributorsAutocomplete'
import i18n from 'simple-react-i18n'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import ProgressCard from '../../../components/card/ProgressCard'
import Input from '../../../components/forms/Input'
import RadioButtons from '../../../components/forms/RadioButtons'
import Select from '../../../components/forms/Select'
import ContributorAction from '../../../referencial/components/contributor/actions/ContributorAction'
import SimpleDatePicker from '../../../components/forms/SimpleDatePicker'
import Checkbox from '../../../components/forms/Checkbox'
import HydrometryAction from '../../../hydrometry/actions/HydrometryAction'
import Job from '../../../import/dto/Job'
import ContactAction from '../../../referencial/components/contact/actions/ContactAction'
import { FILTERS, STATIONS, getStationModes } from 'utils/JobUtils'
import { Button, Dialog, DialogActions, DialogContent, Grid } from '@mui/material'
import StationsPointPrelTable from '../../../station/components/StationsPointPrelTable'
import StationAction from '../../../station/actions/StationAction'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { groupBy, mapValues, uniq } from 'lodash'
import moment from 'moment'

const ALL = 'ALL'
const CUSTOM = 'CUSTOM'
const DIFF = 'DIFF'
const DEFAULT_HEIGHT_COEFFICIENT = 1000
const HEIGHT_COEFFICIENT_OPTIONS = [
    { code: 1000, name: '1000 (m)' },
    { code: 100, name: '100 (dm)' },
    { code: 10, name: '10 (cm)' },
    { code: 1, name: '1 (mm)' },
]

const ExportHydroportailPanel = ({
    job = {},
    onChangeJob = () => {},
    isEditMode = false,
}) => {
    const {
        hydrometricStations,
        contributors,
        // cities,
        contacts,
        pointsPrelList,
        hydrologicalContributors,
    } = useSelector(store => ({
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        contributors: store.ContributorReducer.contributors,
        // cities: store.CityReducer.cities,
        contacts: store.ContactReducer.contacts,
        pointsPrelList: store.StationReducer.pointsPrelByType.hydrometry || [],
        hydrologicalContributors: store.HydrometryReducer.hydrologicalContributors,
    }), shallowEqual)

    const exportModes = [
        { code: ALL, name: i18n.exportHistory },
        { code: CUSTOM, name: i18n.choiseDate },
        { code: DIFF, name: i18n.exportAccordingLastAutoExport },
        // { code: UPDATE_DATE, name: i18n.exportWithUpdateDate },
    ]

    const dispatch = useDispatch()

    const { progress, isLoaded } = useProgressDispatch(() => [
        dispatch(HydrometryAction.fetchHydrometricStations()),
        dispatch(ContributorAction.fetchContributors()),
        // dispatch(CityAction.fetchCities()),
        dispatch(ContactAction.fetchContacts()),
        dispatch(StationAction.fetchStationPointsPrel(STATION_TYPE_NAME.hydrometry)),
        dispatch(HydrometryAction.fetchHydrologicalContributors()),
    ], [])

    const jobFilters = useMemo(() => job.parameters.filters.length ? JSON.parse(job.parameters.filters[0]) : { height: true, flow: true }, [job.parameters.filters])

    const onChangeJobParameter = (changes) => onChangeJob({ parameters: { ...job.parameters, ...changes } })

    const onChangeFilters = (changes) => onChangeJob({ parameters: { ...job.parameters, filters: [JSON.stringify({ ...jobFilters, ...changes })] } })

    const contributorsFormatted = useMemo(() => contributors.filter(c => c.siret?.length > 0).map((c) => ({
        ...c,
        nameFormatted: `${c?.siret} - ${c?.name || ''}`,
    })), [contributors])

    const selectedStations = useMemo(() => hydrometricStations.filter(p => (jobFilters.stations || []).includes(p.id)), [jobFilters.stations, hydrometricStations])

    const [list, setList] = useState(selectedStations)
    const [isOpen, setIsOpen] = useState(false)

    const pointsPrel = useMemo(() => Array.isArray(jobFilters.pointsPrel) ? jobFilters.pointsPrel : [], [jobFilters.pointsPrel])

    const heightCoefficients = useMemo(() => Array.isArray(jobFilters.heightCoefficients) ? jobFilters.heightCoefficients : [], [jobFilters.heightCoefficients])

    const internalReferences = useMemo(() => Array.isArray(jobFilters.withInternalReference) ? jobFilters.withInternalReference : [], [jobFilters.withInternalReference])

    const now = moment().valueOf()

    const internalReferencesByStation = useMemo(() => {
        const activeLinks = hydrologicalContributors.filter(link => {
            const reference = link.internalReference?.trim()
            return !!reference && (!link.startDate || link.startDate < now) && (!link.endDate || link.endDate >= now)
        })
        return mapValues(groupBy(activeLinks, 'idStation'), links => uniq(links.map(link => link.internalReference.trim())))
    }, [hydrologicalContributors])

    if (!isLoaded) {
        return <ProgressCard progress={ progress } />
    }

    return (
        <>
            <Grid container spacing={1}>
                <Grid size={4}>
                    <Input
                        title={i18n.directory}
                        value={job.parameters.path}
                        onChange={v => onChangeJobParameter({ path: v })}
                        disabled={!isEditMode}
                    />
                </Grid>
                <Grid size={4}>
                    <Select
                        options={exportModes}
                        label={i18n.exportMode}
                        onChange={v => onChangeFilters({ mode: v })}
                        value={jobFilters.mode}
                        disabled={!isEditMode}
                        tooltip={i18n.exportQesoutTip}
                    />
                </Grid>
                {(() => {
                    if (jobFilters.mode === CUSTOM) {
                        return [
                            // eslint-disable-next-line react/jsx-key
                            <Grid size={2}>
                                <SimpleDatePicker
                                    label={i18n.startDate}
                                    value={jobFilters.startDate}
                                    onChange={v => onChangeFilters({ startDate: v })}
                                    disabled={!isEditMode}
                                />
                            </Grid>,
                            // eslint-disable-next-line react/jsx-key
                            <Grid size={2}>
                                <SimpleDatePicker
                                    label={i18n.endDate}
                                    value={jobFilters.endDate}
                                    onChange={v => onChangeFilters({ endDate: v })}
                                    disabled={!isEditMode}
                                />
                            </Grid>,
                        ]
                    }
                    // if (jobFilters.mode === UPDATE_DATE) {
                    //     return (
                    //         <SimpleDatePicker
                    //             col={4}
                    //             label={i18n.updateDate}
                    //             value={jobFilters.updateDateModeDate}
                    //             onChange={v => onChangeFilters({ updateDateModeDate: v })}
                    //             disabled={!isEditMode}
                    //         />
                    //     )
                    // }
                    return <Grid size={4}/>
                })()}
            </Grid>
            <Grid container spacing={1}>
                <Grid size={4}>
                    <MultiContributorsAutocomplete
                        label={`${i18n.contributor} ${i18n.sender}`}
                        values={jobFilters.sender}
                        onChange={c => onChangeFilters({ sender: c })}
                        options={contributorsFormatted}
                        keyLabel='nameFormatted'
                        disabled={!isEditMode}
                        obligatory
                    />
                </Grid>
                <Grid size={4}>
                    <Select
                        options={contacts}
                        label={`${i18n.contact} ${i18n.sender}`}
                        value={jobFilters.senderContact}
                        nullLabel='&nbsp;'
                        displayWithCode
                        onChange={c => onChangeFilters({ senderContact: c })}
                        disabled={!isEditMode}
                        obligatory
                    />
                </Grid>
                <Grid size={4}/>
            </Grid>
            <Grid container spacing={1} alignItems={'center'}>
                <Grid size={4}>
                    <RadioButtons
                        title={i18n.selectStations}
                        elements={getStationModes()}
                        selected={jobFilters.selectStationsMode || FILTERS}
                        onChange={v => onChangeFilters({ selectStationsMode: v })}
                        disabled={!isEditMode}
                    />
                </Grid>
                <Grid size={2}>
                    <Checkbox
                        label='Données brutes'
                        checked={jobFilters.bruteMeasures}
                        onChange={c => onChangeFilters({ bruteMeasures: c })}
                        disabled={!isEditMode}
                    />
                </Grid>
                <Grid size={2}>
                    <Checkbox
                        label={i18n.validData}
                        checked={jobFilters.validMeasures}
                        onChange={c => onChangeFilters({ validMeasures: c })}
                        disabled={!isEditMode}
                    />
                </Grid>
                <Grid size={2}>
                    <Checkbox
                        label='Hauteurs'
                        checked={jobFilters.height}
                        onChange={c => onChangeFilters({ height: c })}
                        disabled={!isEditMode}
                    />
                </Grid>
                <Grid size={2}>
                    <Checkbox
                        label='Débits'
                        checked={jobFilters.flow}
                        onChange={c => onChangeFilters({ flow: c })}
                        disabled={!isEditMode}
                    />
                </Grid>
                <Grid size={2}>
                    <Select
                        label={i18n.heightCoefficient}
                        tooltip={i18n.heightCoefficientHelp}
                        value={jobFilters.heightCoefficient ?? DEFAULT_HEIGHT_COEFFICIENT}
                        options={HEIGHT_COEFFICIENT_OPTIONS}
                        keyValue='code'
                        keyLabel='name'
                        noSort
                        noNullValue
                        onChange={v => onChangeFilters({ heightCoefficient: v })}
                        disabled={!isEditMode || !jobFilters.height}
                    />
                </Grid>
                {jobFilters.selectStationsMode === STATIONS && (
                    <Grid container size={2} alignItems='center'>
                        <Button
                            variant='outlined'
                            onClick={() => onChangeFilters({
                                heightCoefficients: selectedStations.map(s => ({ idStation: s.id, coefficient: jobFilters.heightCoefficient ?? DEFAULT_HEIGHT_COEFFICIENT })),
                            })}
                            disabled={!isEditMode || !jobFilters.height || !selectedStations.length}
                        >
                            {i18n.applyToAllStations}
                        </Button>
                    </Grid>
                )}
                {!jobFilters.selectStationsMode || jobFilters.selectStationsMode === FILTERS ? (
                    <Grid size={4}>
                        <SimpleFilterSelect
                            onChange={(_, f) => onChangeFilters({ stationsFilter: f && f !== -1 ? f : null })}
                            stations={hydrometricStations}
                            stationType={STATION_TYPE_NAME.hydrometry}
                            disabled={!isEditMode}
                            value= {jobFilters.stationsFilter}
                        />
                    </Grid>
                ) : (
                    <Grid size={12}>
                        <StationsPointPrelTable
                            stations={selectedStations}
                            pointsPrel={pointsPrel}
                            points={pointsPrelList}
                            onChange={v => onChangeFilters({ pointsPrel: v })}
                            disabled={!isEditMode}
                            actions={[{ icon: 'edit', onClick: () => setIsOpen(true), color: 'white' }]}
                            codeOptions={internalReferencesByStation}
                            selectedCodes={internalReferences}
                            onChangeCodes={v => onChangeFilters({ withInternalReference: v })}
                            defaultCoefficient={jobFilters.heightCoefficient ?? DEFAULT_HEIGHT_COEFFICIENT}
                            coefficientOptions={HEIGHT_COEFFICIENT_OPTIONS}
                            coefficients={jobFilters.height ? heightCoefficients : undefined}
                            onChangeCoefficients={jobFilters.height ? v => onChangeFilters({ heightCoefficients: v }) : undefined}
                        />
                    </Grid>
                )}
            </Grid>
            <Dialog open={isOpen} fullWidth maxWidth='lg'>
                <DefaultDialogTitle
                    onClose={() => {
                        setList(selectedStations)
                        setIsOpen(false)
                    }}
                    title={i18n.selectStationsToBeExported}
                />
                <DialogContent>
                    <SelectedElementsModal
                        selectedList={list}
                        onChange={setList}
                        list={hydrometricStations}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        variant='contained'
                        onClick={() => {
                            onChangeFilters({ stations: list.map(p => p.id) })
                            setIsOpen(false)
                        }}
                    >
                        {i18n.save}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

ExportHydroportailPanel.propTypes = {
    job: PropTypes.instanceOf(Job).isRequired,
    onChangeJob: PropTypes.func.isRequired,
    isEditMode: PropTypes.bool,
}

export default ExportHydroportailPanel